import { FC, memo, PropsWithChildren, ReactNode, useCallback } from 'react';

import { Text, VSpacing } from '@hh.ru/magritte-ui';

import FormatMoney, { FormatMoneyRenderFunction } from 'src/components/FormatMoney';
import { PriceCartProductType } from 'src/models/price/priceCart';
import { NON_BREAKING_SPACE } from 'src/utils/constants/symbols';

import getBrandedVacancyTemplateCount from 'src/components/Price/PriceCart/PriceCartProduct/Branding/getBrandedVacancyTemplateCount';
import RemoveButton from 'src/components/Price/PriceCart/PriceCartProduct/RemoveButton';

import styles from './price-cart-item-hh.less';

export interface PriceCartItemHHProps {
    dataQa?: string;
    product: PriceCartProductType;
    title: ReactNode;
    list?: { key: string; element: ReactNode }[];
}

const PriceCartItemHH: FC<PriceCartItemHHProps & PropsWithChildren> = ({ dataQa, product, list, title, children }) => {
    const brandedVacancyTemplateCount = getBrandedVacancyTemplateCount(product);
    const hasBrandedVacancyTemplate = !!brandedVacancyTemplateCount;

    const renderDiscount = useCallback<FormatMoneyRenderFunction>(
        (amount, currencyNode) => (
            <>
                <span data-qa="cart-item__discount-amount">{amount}</span>
                {NON_BREAKING_SPACE}
                <span data-qa="cart-item__discount-currency">{currencyNode}</span>
            </>
        ),
        []
    );

    return (
        <>
            <div data-qa={dataQa}>
                <div className={styles.title}>
                    <Text typography="subtitle-1-semibold" className={styles.titleText}>
                        {title}
                    </Text>
                    <div className={styles.titleRight}>
                        <div className={styles.priceWrapper}>
                            <Text typography="subtitle-1-semibold" data-qa="cart-item__total-cost">
                                <FormatMoney
                                    inCents
                                    currency={product.currency}
                                    render={(amount, currencyNode) => (
                                        <>
                                            <span data-qa="money__amount">{amount}</span>
                                            {NON_BREAKING_SPACE}
                                            <span data-qa="money__currency">{currencyNode}</span>
                                        </>
                                    )}
                                >
                                    {product.price}
                                </FormatMoney>
                            </Text>
                            {product.scaledPrice && (
                                <Text typography="label-3-regular" style="secondary">
                                    <span className={styles.cartItemDiscount} data-qa="cart-item__discount">
                                        <FormatMoney inCents={true} currency={product.currency} render={renderDiscount}>
                                            {product.scaledPrice}
                                        </FormatMoney>
                                    </span>
                                </Text>
                            )}
                        </div>
                        <span className={styles.removeBtnWrapper}>
                            <RemoveButton
                                itemId={product.itemId}
                                onClick={() => {
                                    if (hasBrandedVacancyTemplate) {
                                        window.dispatchEvent(new CustomEvent('HH-Branding-Price-Backet-Remove-Item'));
                                    }
                                }}
                            />
                        </span>
                    </div>
                </div>
                {/* TODO: https://jira.hh.ru/browse/HH-217834*/}
                {children && (
                    <>
                        <VSpacing default={8} />
                        <Text typography="label-3-regular" style="secondary">
                            <ul className={styles.subProductList}>{children}</ul>
                        </Text>
                    </>
                )}
                {list && (
                    <>
                        <VSpacing default={8} />
                        <Text typography="label-3-regular" style="secondary">
                            <ul className={styles.subProductList}>
                                {list.map(({ element, key }) => (
                                    <li className={styles.subProduct} key={key}>
                                        {element}
                                    </li>
                                ))}
                            </ul>
                        </Text>
                    </>
                )}
            </div>
        </>
    );
};

export default memo(PriceCartItemHH);
